import { useTranslation, type TFunction } from "react-i18next";
import { Dialog } from "@mui/material";
import {
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconChevronLeft,
  IconChevronRight,
  IconDownload,
  IconChartLine,
  IconSparkles,
  IconTable,
  IconX,
} from "@tabler/icons-react";
import LegendText from "../../../components/post/LegendText";
import { Button } from "../../../components/ui/Button";
import { PanZoomImage } from "../../../components/ui/PanZoomImage";
import { ZoomableImage } from "../../../components/ui/ZoomableImage";
import { cn } from "../../../lib/utils";
import type { ArticlePageArticle } from "../../../features/article/hooks/useArticleData";

type ArticleImage = ArticlePageArticle["images"][number];

/** A figure or table of the paper, numbered the way the paper numbers it. */
export type ArticleFigure = {
  image: ArticleImage;
  kind: "figure" | "table";
  // "Fig. 2", "Table 1" (see figureNumbers), or "Figure" for an image the
  // paper does not number.
  label: string;
  // The paper's number for it, null when the paper does not number it.
  number: number | null;
  // The page of the original PDF it was lifted from, when the parser knew it.
  page: number | null;
};

// The number a legend opens with: "FIGURE 2 | …", "Fig. 3. …", "Table 1 …".
const LEGEND_NUMBER = /^\s*(?:fig(?:ure)?\.?|table)\s*(\d+)/i;

function legendNumber(legend: string | null): number | null {
  if (legend === null) return null;
  const match = LEGEND_NUMBER.exec(legend);
  if (match === null) return null;
  const digits = match[1];
  if (digits === undefined) {
    throw new Error("should not happen: LEGEND_NUMBER has one group");
  }
  return Number(digits);
}

function kindOf(image: { assetType: string | null }): ArticleFigure["kind"] {
  return image.assetType === "table" ? "table" : "figure";
}

/**
 * The kind and number of each image, in reading order. The parser tags a table
 * `assetType: "table"`; everything else, including the images it could not
 * type, is a figure.
 *
 * The chat is asked about "Fig. 2" of the paper, so a number must be the
 * paper's. When the legends of a kind state numbers, those are the numbers,
 * and an image of that kind whose legend states none (a graphical abstract)
 * gets none: counting it would give it a number the paper uses for another
 * figure. When no legend of a kind states a number, the images of that kind
 * are counted in reading order.
 */
export function figureNumbers(
  images: { assetType: string | null; legend: string | null }[]
): { kind: ArticleFigure["kind"]; number: number | null }[] {
  const stated = images.map((image) => legendNumber(image.legend));
  const kindsWithStatedNumbers = new Set(
    images
      .filter((_, index) => stated[index] !== null)
      .map((image) => kindOf(image))
  );
  const counts = { figure: 0, table: 0 };
  return images.map((image, index) => {
    const kind = kindOf(image);
    counts[kind] += 1;
    if (kindsWithStatedNumbers.has(kind)) {
      const number = stated[index];
      if (number === undefined) {
        throw new Error(
          `should not happen: no legend read for ${String(index)}`
        );
      }
      return { kind, number };
    }
    return { kind, number: counts[kind] };
  });
}

function figureLabel(
  t: TFunction,
  kind: ArticleFigure["kind"],
  number: number | null
): string {
  switch (kind) {
    case "table":
      return number === null ? t("Table") : t("Table {{number}}", { number });
    case "figure":
      return number === null ? t("Figure") : t("Fig. {{number}}", { number });
    default:
      throw new Error(kind satisfies never);
  }
}

/** The paper's figures and tables, labelled for the reader. */
export function articleFigures(
  images: ArticleImage[],
  t: TFunction
): ArticleFigure[] {
  const numbers = figureNumbers(images);
  return images.map((image, index) => {
    const numbered = numbers[index];
    if (numbered === undefined) {
      throw new Error(
        `should not happen: no number for image ${String(index)}`
      );
    }
    return {
      image,
      kind: numbered.kind,
      label: figureLabel(t, numbered.kind, numbered.number),
      number: numbered.number,
      page: image.sourcePage,
    };
  });
}

// A license under which the reader may take the figure away.
function canRedistribute(license: string | null): boolean {
  if (license === null) return false;
  return license.startsWith("cc-") || license === "public-domain";
}

function FigurePlaceholder({
  kind,
  size,
}: {
  kind: ArticleFigure["kind"];
  size: "sm" | "md";
}): React.JSX.Element {
  const Icon = kind === "table" ? IconTable : IconChartLine;
  return (
    <Icon
      stroke={1.5}
      className={cn(
        "text-ds-black opacity-35",
        size === "sm" ? "size-4" : "size-6"
      )}
    />
  );
}

function FigureThumb({
  figure,
  className,
  placeholderSize,
}: {
  figure: ArticleFigure;
  className: string;
  placeholderSize: "sm" | "md";
}): React.JSX.Element {
  return (
    <span
      className={cn(
        "flex items-center justify-center overflow-hidden bg-ds-slate-50",
        className
      )}
    >
      {figure.image.url === "" ? (
        <FigurePlaceholder kind={figure.kind} size={placeholderSize} />
      ) : (
        <img
          src={figure.image.url}
          alt={figure.label}
          loading="lazy"
          className="size-full object-contain"
        />
      )}
    </span>
  );
}

function PageSuffix({
  page,
}: {
  page: number | null;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  if (page === null) return null;
  return (
    <span className="text-caption text-ds-slate-600">
      {` · ${t("p. {{page}}", { page })}`}
    </span>
  );
}

// ── Gallery ──────────────────────────────────────────────────────────────

/** The "Figures & tables" grid of the reading column: one tile per figure. */
export function FigureGallery({
  figures,
  openIndex,
  onOpen,
}: {
  figures: ArticleFigure[];
  // The figure shown in the panel or the viewer, outlined in the grid.
  openIndex: number | null;
  onOpen: (index: number) => void;
}): React.JSX.Element {
  return (
    // 232px tiles that wrap (Figma "grid · 3 colonnes"): three across the
    // 720px column, two once the figure panel narrows it.
    <div>
      <div className="flex flex-wrap gap-x-3 gap-y-5">
        {figures.map((figure, index) => (
          <button
            key={figure.image.id}
            type="button"
            onClick={() => {
              onOpen(index);
            }}
            className="group flex w-58 cursor-pointer flex-col gap-2 border-none bg-transparent p-0 text-left"
          >
            <FigureThumb
              figure={figure}
              placeholderSize="md"
              className={cn(
                "h-39 w-full rounded-desktop-figure border transition-colors",
                index === openIndex
                  ? "border-ds-mint-800"
                  : "border-ds-slate-100 group-hover:border-ds-slate-400"
              )}
            />
            <span className="flex items-center gap-1 whitespace-nowrap">
              <span className="text-footnote font-semibold text-ds-black">
                {figure.label}
              </span>
              <PageSuffix page={figure.page} />
            </span>
            {figure.image.legend === null ? null : (
              <span className="line-clamp-2 text-footnote text-ds-slate-600">
                <LegendText>{figure.image.legend}</LegendText>
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Shared pieces of the panel and the viewer ────────────────────────────

function IconFrameButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex cursor-pointer items-center justify-center rounded-lg border border-ds-slate-100 bg-ds-white p-1.5 text-ds-black transition-colors hover:bg-ds-slate-50 disabled:cursor-not-allowed disabled:opacity-40 [&>svg]:size-5"
    >
      {children}
    </button>
  );
}

function FigureTitle({
  figure,
  index,
  count,
}: {
  figure: ArticleFigure;
  index: number;
  count: number;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex min-w-0 items-center gap-2 whitespace-nowrap">
      <span className="truncate text-ui-body font-semibold text-ds-black">
        {figure.page === null
          ? figure.label
          : t("{{figure}} · p. {{page}}", {
              figure: figure.label,
              page: figure.page,
            })}
      </span>
      <span className="text-footnote text-ds-slate-600">
        {t("{{position}} of {{total}}", {
          position: index + 1,
          total: count,
        })}
      </span>
    </div>
  );
}

function MetaLabel({ children }: { children: string }): React.JSX.Element {
  return (
    <span className="text-footnote font-medium tracking-wider text-ds-slate-600 uppercase">
      {children}
    </span>
  );
}

/**
 * The two questions a reader asks of a figure, worded for Chat with paper.
 * The chat only reads the paper's text, so a question names the figure the
 * way the text does: by its number, else by its legend. A figure with neither
 * cannot be named, and gets no questions.
 */
function figureQuestions(
  t: TFunction,
  figure: ArticleFigure
): { explanation: string; keyNumbers: string } | null {
  if (figure.number !== null) {
    return {
      explanation: t("Explain {{figure}} of this paper.", {
        figure: figure.label,
      }),
      keyNumbers: t("What are the key numbers in {{figure}} of this paper?", {
        figure: figure.label,
      }),
    };
  }
  const legend = figure.image.legend;
  if (legend === null) return null;
  switch (figure.kind) {
    case "figure":
      return {
        explanation: t(
          'Explain the figure captioned "{{legend}}" in this paper.',
          { legend }
        ),
        keyNumbers: t(
          'What are the key numbers in the figure captioned "{{legend}}" in this paper?',
          { legend }
        ),
      };
    case "table":
      return {
        explanation: t(
          'Explain the table captioned "{{legend}}" in this paper.',
          { legend }
        ),
        keyNumbers: t(
          'What are the key numbers in the table captioned "{{legend}}" in this paper?',
          { legend }
        ),
      };
    default:
      throw new Error(figure.kind satisfies never);
  }
}

/** The questions about a figure, handed to Chat with paper. */
function AskAboutFigure({
  figure,
  questions: asked,
  layout,
  onAsk,
}: {
  figure: ArticleFigure;
  questions: { explanation: string; keyNumbers: string };
  layout: "row" | "column";
  onAsk: (question: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const questions = [
    {
      label:
        figure.kind === "table"
          ? t("Explain this table")
          : t("Explain this figure"),
      question: asked.explanation,
    },
    { label: t("What are the key numbers?"), question: asked.keyNumbers },
  ];
  return (
    // Figma: in the panel the label stands 16px above a row of buttons
    // inset by 8px; in the viewer's column everything is 6px apart.
    <div
      className={cn("flex flex-col", layout === "row" ? "gap-4" : "gap-1.5")}
    >
      <span className="text-footnote font-medium text-ds-slate-600">
        {figure.kind === "table"
          ? t("Ask about this table")
          : t("Ask about this figure")}
      </span>
      <div
        className={cn(
          "flex",
          layout === "row" ? "flex-row flex-wrap gap-2 p-2" : "flex-col gap-1.5"
        )}
      >
        {questions.map((entry) => (
          <Button
            key={entry.label}
            variant="outline"
            size="sm"
            // In a row, a label keeps its width: in a narrow panel the second
            // question goes under the first rather than being cut.
            className={cn(
              "text-ui-body",
              // A row shares its width; a column keeps the 36px height,
              // which a flex basis of 0 would trade for the label's.
              layout === "row" ? "min-w-max flex-1" : "w-full"
            )}
            leftIcon={<IconSparkles className="text-ds-mint-800" />}
            onClick={() => {
              onAsk(entry.question);
            }}
          >
            {entry.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

function ThumbnailStrip({
  figures,
  index,
  size,
  onSelect,
}: {
  figures: ArticleFigure[];
  index: number;
  // `sm`: the panel footer, images only. `md`: the viewer strip, labelled.
  size: "sm" | "md";
  onSelect: (index: number) => void;
}): React.JSX.Element {
  return (
    <div className="flex min-w-0 gap-2 overflow-x-auto">
      {figures.map((figure, thumbIndex) => (
        <button
          key={figure.image.id}
          type="button"
          aria-label={figure.label}
          aria-current={thumbIndex === index}
          onClick={() => {
            onSelect(thumbIndex);
          }}
          className="flex shrink-0 cursor-pointer flex-col items-center gap-1.5 border-none bg-transparent p-0"
        >
          <FigureThumb
            figure={figure}
            placeholderSize="sm"
            className={cn(
              size === "sm"
                ? "aspect-3/2 w-20.5 rounded-md"
                : "h-16 w-24 rounded-lg",
              thumbIndex === index
                ? size === "sm"
                  ? "border border-ds-mint-800"
                  : "border-2 border-ds-black"
                : "border border-ds-slate-100"
            )}
          />
          {size === "md" ? (
            <span
              className={cn(
                "text-footnote whitespace-nowrap",
                thumbIndex === index
                  ? "font-semibold text-ds-black"
                  : "text-ds-slate-600"
              )}
            >
              {figure.label}
            </span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

// ── Panel ────────────────────────────────────────────────────────────────

/**
 * One figure at a time, docked right of the text (Figma "Figure panel · docké
 * à droite 560"). The full screen button or F enlarges any figure, and so
 * does a click on an image; in the panel a table is read in place, zoomed and
 * scrolled sideways, since it is wider than the panel. Under the 1280px grid the panel takes at most half
 * the page, so the text beside it stays readable.
 */
export function FigurePanel({
  figures,
  index,
  onSelect,
  onEnlarge,
  onClose,
  onAsk,
}: {
  figures: ArticleFigure[];
  index: number;
  onSelect: (index: number) => void;
  onEnlarge: () => void;
  onClose: () => void;
  // Null where the page offers no chat on this paper.
  onAsk: ((question: string) => void) | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const figure = figures[index];
  if (figure === undefined) {
    throw new Error(`should not happen: no figure at ${String(index)}`);
  }
  const questions = figureQuestions(t, figure);
  return (
    // 8px under the top bar, where the reading body starts (Figma "Section").
    <aside className="mt-2 flex w-article-figure-panel max-w-1/2 shrink-0 flex-col border-l border-ds-slate-100 bg-ds-white">
      <div className="flex items-center justify-between gap-3 border-b border-ds-slate-100 py-3 pr-4 pl-5">
        <FigureTitle figure={figure} index={index} count={figures.length} />
        <div className="flex shrink-0 items-center gap-1.5">
          <IconFrameButton
            label={t("Previous figure")}
            disabled={index === 0}
            onClick={() => {
              onSelect(index - 1);
            }}
          >
            <IconChevronLeft stroke={1.5} />
          </IconFrameButton>
          <IconFrameButton
            label={t("Next figure")}
            disabled={index === figures.length - 1}
            onClick={() => {
              onSelect(index + 1);
            }}
          >
            <IconChevronRight stroke={1.5} />
          </IconFrameButton>
          <span className="h-5 w-px bg-ds-slate-100" />
          <span className="group relative flex">
            <IconFrameButton
              label={t("Full screen")}
              disabled={false}
              onClick={onEnlarge}
            >
              <IconArrowsMaximize stroke={1.5} />
            </IconFrameButton>
            <span
              aria-hidden
              // Figma "Tooltip · Label" with its Kbd.
              className="pointer-events-none absolute top-full right-0 z-10 mt-2 hidden h-8.5 items-center gap-2 rounded-lg bg-ds-black/88 px-2.5 py-1.5 whitespace-nowrap shadow-popover backdrop-blur-xs group-hover:flex group-has-focus-visible:flex"
            >
              <span className="text-desktop-meta text-ds-white">
                {t("Full screen")}
              </span>
              <kbd className="flex h-4.5 items-center rounded-md bg-ds-slate-100/82 px-1.5 font-dm-sans text-caption text-ds-slate-700">
                F
              </kbd>
            </span>
          </span>
          <IconFrameButton
            label={t("Close")}
            disabled={false}
            onClick={onClose}
          >
            <IconX stroke={1.5} />
          </IconFrameButton>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
        {figure.kind === "table" ? (
          <ZoomableImage
            key={figure.image.id}
            src={figure.image.url}
            alt={figure.label}
            hint={t("Wide table: scroll sideways or zoom out")}
            zoomOutLabel={t("Zoom out")}
            zoomInLabel={t("Zoom in")}
          />
        ) : (
          <button
            type="button"
            aria-label={t("Show full screen")}
            onClick={onEnlarge}
            className="w-full cursor-zoom-in border-none bg-transparent p-0"
          >
            {figure.image.url === "" ? (
              <FigureThumb
                figure={figure}
                placeholderSize="md"
                className="aspect-4/3 w-full rounded-desktop-figure"
              />
            ) : (
              // The panel's width, its proportions kept (Figma "figure · FIT").
              <img
                key={figure.image.id}
                src={figure.image.url}
                alt={figure.label}
                className="block h-auto w-full rounded-desktop-figure bg-ds-slate-50 object-contain"
              />
            )}
          </button>
        )}
        {figure.image.legend === null ? null : (
          <p className="m-0 text-ui-body text-ds-black">
            <LegendText>{figure.image.legend}</LegendText>
          </p>
        )}
        {onAsk === null || questions === null ? null : (
          <>
            <span className="h-px w-full shrink-0 bg-ds-slate-100" />
            <AskAboutFigure
              figure={figure}
              questions={questions}
              layout="row"
              onAsk={onAsk}
            />
          </>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-ds-slate-100 px-5 py-3">
        <ThumbnailStrip
          figures={figures}
          index={index}
          size="sm"
          onSelect={onSelect}
        />
        <span className="shrink-0 text-caption whitespace-pre text-ds-slate-600">
          {"← →  ·  Esc"}
        </span>
      </div>
    </aside>
  );
}

// ── Viewer ───────────────────────────────────────────────────────────────

/**
 * A figure full screen, never cropped, with its legend and source beside it
 * (Figma "Figure viewer · modal").
 */
export function FigureViewer({
  figures,
  index,
  journalName,
  license,
  licenseLabel,
  onSelect,
  onClose,
  onAsk,
}: {
  figures: ArticleFigure[];
  index: number;
  journalName: string | null;
  license: string | null;
  licenseLabel: string | null;
  onSelect: (index: number) => void;
  onClose: () => void;
  // Null where the page offers no chat on this paper.
  onAsk: ((question: string) => void) | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const figure = figures[index];
  if (figure === undefined) {
    throw new Error(`should not happen: no figure at ${String(index)}`);
  }
  const questions = figureQuestions(t, figure);
  const source = [
    journalName,
    figure.page === null
      ? null
      : t("page {{page}} of the original article", { page: figure.page }),
    licenseLabel,
  ].filter((part) => part !== null);

  return (
    <Dialog
      open
      onClose={onClose}
      // Esc is the page's own key (it steps back to the panel the figure was
      // enlarged from), so the dialog must not close on it as well.
      disableEscapeKeyDown
      maxWidth={false}
      PaperProps={{
        // Fills the window but for a 16px margin: the dialog container is a
        // centred flex row, so the paper grows along it and stretches across.
        className:
          "m-4! max-h-none! max-w-none! flex-1 self-stretch rounded-2xl! shadow-search-palette! flex flex-col overflow-hidden bg-ds-white",
      }}
    >
      <div className="flex items-center justify-between gap-3 border-b border-ds-slate-100 py-3 pr-4 pl-5">
        <FigureTitle figure={figure} index={index} count={figures.length} />
        <div className="flex shrink-0 items-center gap-2">
          {canRedistribute(license) ? (
            <a
              href={figure.image.url}
              download
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t("Download")}
              className="flex items-center justify-center rounded-lg border border-ds-slate-100 bg-ds-white p-1.5 text-ds-black hover:bg-ds-slate-50"
            >
              <IconDownload stroke={1.5} className="size-5" />
            </a>
          ) : null}
          {/* Full screen is entered from the panel, so leaving it is the way
              out (Figma: the cross only when the viewer opens straight from
              the article, which it never does). */}
          <IconFrameButton
            label={t("Exit full screen")}
            disabled={false}
            onClick={onClose}
          >
            <IconArrowsMinimize stroke={1.5} />
          </IconFrameButton>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        <div className="relative flex min-w-0 flex-1 items-center justify-center bg-ds-slate-50">
          {figure.image.url === "" ? (
            <FigurePlaceholder kind={figure.kind} size="md" />
          ) : (
            <PanZoomImage
              key={figure.image.id}
              src={figure.image.url}
              alt={figure.label}
              zoomOutLabel={t("Zoom out")}
              zoomInLabel={t("Zoom in")}
              failure={{
                message: t("Something went wrong. Please try again."),
                retryLabel: t("Try again"),
              }}
              fitLabel={null}
              toolbarStart={null}
              toolbarEnd={null}
              onLoad={null}
            />
          )}
          <span className="absolute top-1/2 left-4 -translate-y-1/2">
            <IconFrameButton
              label={t("Previous figure")}
              disabled={index === 0}
              onClick={() => {
                onSelect(index - 1);
              }}
            >
              <IconChevronLeft stroke={1.5} />
            </IconFrameButton>
          </span>
          <span className="absolute top-1/2 right-4 -translate-y-1/2">
            <IconFrameButton
              label={t("Next figure")}
              disabled={index === figures.length - 1}
              onClick={() => {
                onSelect(index + 1);
              }}
            >
              <IconChevronRight stroke={1.5} />
            </IconFrameButton>
          </span>
        </div>
        <div className="flex w-gpt-context-panel shrink-0 flex-col gap-5 overflow-y-auto border-l border-ds-slate-100 p-6">
          {figure.image.legend === null ? null : (
            <div className="flex flex-col gap-1.5">
              <MetaLabel>{t("Legend")}</MetaLabel>
              <p className="m-0 text-desktop-body text-ds-black">
                <LegendText>{figure.image.legend}</LegendText>
              </p>
            </div>
          )}
          {source.length === 0 ? null : (
            <div className="flex flex-col gap-1.5">
              <MetaLabel>{t("Source")}</MetaLabel>
              <p className="m-0 text-ui-body text-ds-black">
                {source.join(" · ")}
              </p>
            </div>
          )}
          {onAsk === null || questions === null ? null : (
            <AskAboutFigure
              figure={figure}
              questions={questions}
              layout="column"
              onAsk={onAsk}
            />
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-ds-slate-100 px-5 py-3">
        <ThumbnailStrip
          figures={figures}
          index={index}
          size="md"
          onSelect={onSelect}
        />
        <span className="shrink-0 text-caption text-ds-slate-600">
          {t("← → to navigate · Esc to exit full screen")}
        </span>
      </div>
    </Dialog>
  );
}
